import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlertCircle } from '#src/atoms/icons/system/iconAlertCircle.ts';
import { iconCheckCircle } from '#src/atoms/icons/system/iconCheckCircle.ts';
import { iconLock } from '#src/atoms/icons/system/iconLock.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BodyS,
  HeadlineXS,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import {
  SecurityCheckTab,
  useAccountSecurityCheckContext,
} from '#src/contexts/accountSecurityCheck/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useRecoveryOptions } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { glossary } from '#src/lib/glossary.ts';

const Badge = styled.div<{ enabled: boolean }>`
  color: ${({ enabled }) =>
    enabled ? 'var(--c-onBase-success)' : 'var(--c-red-300)'};
  display: inline-block;
  padding: 0 var(--half-unit);
  gap: var(--double-unit);
  border: solid 1px currentColor;
  border-radius: var(--half-unit);
`;

export const TwoFA = () => {
  const { setSecurityCheckTab } = useAccountSecurityCheckContext();
  const { hasActiveRecoveryEmail } = useRecoveryOptions();

  const { currentUser } = useCurrentUserContext();
  if (!currentUser) return null;
  const { otpRequiredForLogin } = currentUser;

  return (
    <Vertical gap={2}>
      <Vertical>
        <Horizontal>
          {otpRequiredForLogin ? (
            <FontAwesomeIcon
              icon={iconCheckCircle}
              color="var(--c-onBase-success)"
            />
          ) : (
            <FontAwesomeIcon icon={iconAlertCircle} color="var(--c-red-500)" />
          )}
          <HeadlineXS as="h6" color="var(--c-white)">
            <FormattedMessage defaultMessage="Two-Factor Authentication (2FA)" />
          </HeadlineXS>
        </Horizontal>
        {!otpRequiredForLogin && (
          <BodyS color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="Add an extra layer of security to your account." />
          </BodyS>
        )}
      </Vertical>
      <Horizontal>
        <BodyM color="var(--c-white)">
          <FormattedMessage defaultMessage="Authenticator app" />
        </BodyM>
        <Badge enabled={otpRequiredForLogin}>
          <LabelS bold>
            {otpRequiredForLogin ? (
              <FormattedMessage {...glossary.enabled} />
            ) : (
              <FormattedMessage {...glossary.disabled} />
            )}
          </LabelS>
        </Badge>
      </Horizontal>
      {!otpRequiredForLogin && (
        <Vertical gap={1}>
          <Button
            size="small"
            color="secondary"
            disabled={!hasActiveRecoveryEmail}
            onClick={() => setSecurityCheckTab(SecurityCheckTab.ENABLE_2FA)}
            startIcon={<FontAwesomeIcon icon={iconLock} />}
          >
            <FormattedMessage defaultMessage="Enable 2FA" />
          </Button>
          {!hasActiveRecoveryEmail && (
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage {...glossary.addRecoveryEmailBeforeTwoFa} />
            </BodyS>
          )}
        </Vertical>
      )}
    </Vertical>
  );
};
